import React from 'react';
import { IonContent } from '@ionic/react';
import styled from 'styled-components';
import { Case } from '../../../services/buildfire/rdb/cases';
import { Report } from '../../../services/buildfire/rdb/reports';
import Typography from '../../common/Typography';
import CaseInfoHeader from '../CaseInfoHeader';
import PatientInfo from '../PatientInfo';
import SitesList from '../site/SitesList';
import SubmitterInfo from '../SubmitterInfo';
import ReportItemInfo from './ReportItemInfo';
import PaymentInfo from '../PaymentInfo';
import AlertBox from '../../common/AlertBox';
import PathologistInfo from './PathologistInfo';
import { CustomerActionItems } from '../../../views/customer/Cases';
import { LOADING_STATE } from '../../../util/loading';

interface ReportProps {
  case: Case;
  report: Report;
  clinicName: string;
  clinicEmail: string;
  isAdmin: boolean;
}

const Padding = styled.div`
  padding: 0 16px;
`;

const ReportContainer = styled.div`
  margin-top: 16px;
  margin-bottom: 32px;
`;

const ReportComponent: React.FC<ReportProps> = ({
  case: c,
  report,
  clinicEmail,
  clinicName,
  isAdmin,
  children,
  ...props
}) => {
  if (!report) {
    return (
      <Typography variant="primary" fontSize="24px">
        Report not found for case {c.id}
      </Typography>
    );
  }
  return (
    <>
      <CaseInfoHeader
        case={c}
        actionItems={
          <CustomerActionItems case={c} loadingStatus={LOADING_STATE.LOADED} />
        }
        isAdmin={isAdmin}
        showStatusBadge={false}
      />
      <IonContent>
        <Padding>
          <PaymentInfo case={c} />
          {c.acceptedBy && (
            <PathologistInfo
              name={c.acceptedBy.signature}
              completedAt={isAdmin ? c.completedAt : null}
            />
          )}
          <SubmitterInfo
            case={c}
            clinicName={clinicName}
            clinicEmail={clinicEmail}
          />
          <PatientInfo case={c} includeName />
        </Padding>

        <SitesList case={c} userId={c.uid} sites={c.sites} />
        <ReportContainer>
          {report?.items.map((item, i) => (
            <ReportItemInfo key={i} item={item} index={i} />
          ))}

          {!isAdmin && (
            <Padding>
              <AlertBox
                type="info"
                text={
                  <div>
                    <Typography variant="primary" fontSize="16px">
                      If you would like to submit any additional photos or
                      videos from this case within 24 hours at no additional
                      charge, please email them to{' '}
                      <a href="mailto:info@pocketpathologist.com">
                        info@pocketpathologist.com
                      </a>{' '}
                      and an addendum will be emailed back to you.
                    </Typography>

                    <br />

                    <Typography variant="primary" fontSize="16px">
                      If you have any questions regarding this case, please do
                      not hesitate to message me within the app or email{' '}
                      <a href="mailto:info@pocketpathologist.com">
                        info@pocketpathologist.com
                      </a>
                      . Thank you for using Pocket Pathologist!
                    </Typography>
                  </div>
                }
              />
            </Padding>
          )}

          <Padding>{children}</Padding>
        </ReportContainer>
      </IonContent>
    </>
  );
};

export default ReportComponent;
